/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState, useEffect } from "react";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { SessionService } from "../services/SessionService";
import { resolveApiErrorMessage } from "@/shared/utils/apiErrorMessage";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Eye,
  EyeOff,
  Lock,
  AlertCircle,
  Clock,
  XCircle,
  AlertTriangle,
  Check,
  X,
} from "lucide-react";

// UI components
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/shared/components/ui/form";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  CardFooter,
} from "@/shared/components/ui/card";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@/shared/components/ui/alert";
import { useToast } from "@/shared/components/ui/use-toast";
import { LoadingSpinner } from "@/shared/components/ui/loading-spinner";
import {
  useValidateResetToken,
  useResetPassword,
} from "../hooks/usePasswordReset";

// Esquema de validação usando Zod com traduções dinamicas
const createResetPasswordSchema = (t: (key: string) => string) => {
  return z
    .object({
      password: z
        .string()
        .min(8, { message: t("register.validation.passwordMinLength") })
        .regex(/[a-z]/, {
          message: t("register.passwordRequirements.lowerCase"),
        })
        .regex(/[A-Z]/, {
          message: t("register.passwordRequirements.upperCase"),
        })
        .regex(/[0-9]/, { message: t("register.passwordRequirements.number") })
        .regex(/[^a-zA-Z0-9]/, {
          message: t("register.passwordRequirements.specialChar"),
        }),
      confirmPassword: z.string(),
    })
    .refine((data) => data.password === data.confirmPassword, {
      message: t("resetPassword.passwordsDoNotMatch"),
      path: ["confirmPassword"],
    });
};

type ResetPasswordForm = z.infer<ReturnType<typeof createResetPasswordSchema>>;

/**
 * Verifica se a mensagem de erro indica que o token expirou
 * @param message Mensagem de erro retornada pelo backend
 * @returns true se o token estiver expirado
 */
const isTokenExpired = (message?: string): boolean => {
  if (!message) return false;
  return (
    message.toLowerCase().includes("token expirado") ||
    message.toLowerCase().includes("expired")
  );
};

/**
 * Verifica se a mensagem de erro indica que o token já foi utilizado
 * @param message Mensagem de erro retornada pelo backend
 * @returns true se o token já foi utilizado
 */
const isTokenUsed = (message?: string): boolean => {
  if (!message) return false;
  return (
    message.toLowerCase().includes("token já utilizado") ||
    message.toLowerCase().includes("already used") ||
    message.toLowerCase().includes("token substituído")
  );
};

// Função removida: getDetailedErrorMessage - Substituída pela função getErrorDetails

/**
 * Determina o tipo de erro para exibição visual apropriada
 * @param errorObj - Objeto de erro ou mensagem de erro
 * @param t - Função de tradução
 * @returns Detalhes visuais para o erro
 */
const getErrorDetails = (errorObj: any, t: any) => {
  // Se não houver erro, retorna configuração padrão
  if (!errorObj)
    return {
      title: t("resetPassword.invalidToken"),
      description: t("resetPassword.tokenExpiredOrInvalid"),
      icon: <AlertCircle className="h-16 w-16 text-red-500" />,
      colorClass: "bg-red-100",
      iconClass: "text-red-500",
      message: t("resetPassword.genericError"),
    };

  // Extração da mensagem de erro do objeto
  let errorMessage = "";
  if (typeof errorObj === "string") {
    errorMessage = errorObj;
  } else if (errorObj.originalResponse?.message) {
    // Primeiro tenta obter a mensagem da resposta original (caso seja um erro de API)
    errorMessage = errorObj.originalResponse.message;
  } else if (errorObj.message) {
    errorMessage = errorObj.message;
  } else if (errorObj.error) {
    errorMessage = errorObj.error;
  } else if (errorObj.data && errorObj.data.message) {
    errorMessage = errorObj.data.message;
  }

  if (isTokenExpired(errorMessage)) {
    return {
      title: t("resetPassword.tokenExpired"),
      description: t("resetPassword.tokenExpiredDescription"),
      icon: <Clock className="h-16 w-16 text-amber-500" />,
      colorClass: "bg-amber-100",
      iconClass: "text-amber-500",
      message: errorMessage,
    };
  } else if (isTokenUsed(errorMessage)) {
    return {
      title: t("resetPassword.tokenAlreadyUsed"),
      description: t("resetPassword.tokenAlreadyUsedDescription"),
      icon: <XCircle className="h-16 w-16 text-red-500" />,
      colorClass: "bg-red-100",
      iconClass: "text-red-500",
      message: errorMessage,
    };
  } else {
    return {
      title: t("resetPassword.invalidToken"),
      description: t("resetPassword.invalidTokenDescription"),
      icon: <AlertTriangle className="h-16 w-16 text-orange-500" />,
      colorClass: "bg-orange-100",
      iconClass: "text-orange-500",
      message: errorMessage,
    };
  }
};

const ResetPassword = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [searchParams] = useSearchParams();

  // Criar esquema de validação com traduções
  const resetPasswordSchema = createResetPasswordSchema(t);

  // Limpar qualquer sessão existente ao carregar a página - silenciosamente
  useEffect(() => {
    const clearSessionSilently = async () => {
      try {
        // Limpar localStorage e sessionStorage relacionados à autenticação
        localStorage.removeItem("auth_session");
        sessionStorage.removeItem("auth_session");
        localStorage.removeItem("supabase.auth.token");
        localStorage.removeItem("prodgy-auth-state");

        // Limpeza de sessão do Supabase removida - migrado para backend

        // Limpar qualquer token JWT armazenado nas cookies
        document.cookie =
          "sb-access-token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
        document.cookie =
          "sb-refresh-token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
      } catch (error) {
        console.error(t("common.errorClearingSession"), error);
      }
    };

    clearSessionSilently();
  }, [t]);
  // Obter token da URL - não precisamos mais do userId pois o token JWT contém essa informação
  const token = searchParams.get("token");
  // Não precisamos mais obter o userId da URL, ele será extraído do token JWT

  // Estados para controlar a visualização de senha
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [isConfirmPasswordVisible, setIsConfirmPasswordVisible] =
    useState(false);

  // Estado para rastrear o valor da senha em tempo real
  const [currentPassword, setCurrentPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  // Funções de validação individual
  const hasMinLength = currentPassword.length >= 8;
  const hasUpperCase = /[A-Z]/.test(currentPassword);
  const hasLowerCase = /[a-z]/.test(currentPassword);
  const hasNumber = /[0-9]/.test(currentPassword);
  const hasSpecialChar = /[^a-zA-Z0-9]/.test(currentPassword);

  const passwordsMatch =
    currentPassword === confirmPassword && confirmPassword.length > 0;

  // Hook para validar o token. v2 portou o fluxo legacy JWT: o reset exige `userId`,
  // que vem da validação do token (payload do JWT). Por isso validamos nos dois modos.
  const {
    data: tokenValidation,
    error: tokenValidationError,
    isLoading: isValidatingToken,
    isError: isTokenValidationError,
  } = useValidateResetToken(token);

  // Hook para redefinir a senha
  const {
    mutate: resetPassword,
    isPending: isResettingPassword,
    isError: isResetError,
    error: resetError,
  } = useResetPassword();

  // Formulário React Hook Form com validação Zod
  const form = useForm<ResetPasswordForm>({
    resolver: zodResolver(resetPasswordSchema),
    mode: "onChange",
    defaultValues: {
      password: "",
      confirmPassword: "",
    },
  });

  // Estado para controlar se a redefinição foi bem-sucedida
  const [isResetSuccess, _setIsResetSuccess] = useState(false); // Prefixo _ para indicar que não está sendo usado

  // Redirecionar para login após sucesso (com delay para mostrar mensagem)
  useEffect(() => {
    let redirectTimer: NodeJS.Timeout;

    if (isResetSuccess) {
      redirectTimer = setTimeout(() => {
        navigate("/login");
      }, 3000);
    }

    return () => {
      if (redirectTimer) clearTimeout(redirectTimer);
    };
  }, [isResetSuccess, navigate]);

  // Função para invalidar o token JWT no servidor usando o SessionService
  const invalidateServerToken = async () => {
    try {
      // Usar SessionService para garantir que o token seja invalidado no backend
      // SessionService usa métodos estáticos
      const response = await SessionService.logout();

      return response.success;
    } catch (error) {
      console.error(
        "[ResetPassword] Error invalidating token on server:",
        error,
      );
      return false;
    }
  };

  // Função para forçar logout completo
  const forceLogout = async () => {
    try {
      // 1. Remover todos os itens de armazenamento local e de sessão
      const authItems = [
        "auth_session",
        "prodgy-auth-state",
        "prodgy-jwt",
        "supabase.auth.token",
        "sb-access-token",
        "sb-refresh-token",
        "persist:root",
        "prodgy-user",
        "prodgy-organization",
        "auth-token",
        "prodgy-user-data",
      ];

      // Limpar localStorage e sessionStorage
      authItems.forEach((item) => {
        localStorage.removeItem(item);
        sessionStorage.removeItem(item);
      });

      // 2. Limpar todas as cookies relacionadas à autenticação
      const cookiePrefix = ["prodgy-", "sb-", "auth-"];
      document.cookie.split(";").forEach((cookie) => {
        const trimmedCookie = cookie.trim();
        const cookieName = trimmedCookie.split("=")[0];

        // Verificar se o nome do cookie começa com algum dos prefixos
        if (cookiePrefix.some((prefix) => cookieName.startsWith(prefix))) {
          document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
        }
      });

      // 3. Limpeza de sessão do Supabase removida - migrado para backend

      return true;
    } catch (error) {
      console.error(t("common.errorClearingSession"), error);
      return false;
    }
  };

  // Redirecionamento imediato para login
  const redirectToLogin = () => {
    // Usar window.location.replace para garantir que não haja histórico de navegação
    // e que a página atual seja completamente substituída
    window.location.replace("/login");
  };

  // Função para lidar com o envio do formulário
  const onSubmit = (data: ResetPasswordForm) => {

    if (data.password !== data.confirmPassword) {
      toast({
        variant: "destructive",
        title: t("common.error"),
        description: t("resetPassword.passwordsDoNotMatch"),
      });
      return;
    }

    let userId: string | undefined;

    if (
      tokenValidation &&
      tokenValidation.data &&
      typeof tokenValidation.data === "object" &&
      "userId" in tokenValidation.data
    ) {
      userId = tokenValidation.data.userId as string;
    }

    if (!userId) {
      toast({
        variant: "destructive",
        title: t("common.error"),
        description: t("resetPassword.invalidToken"),
      });
      return;
    }

    resetPassword(
      {
        userId,
        token: token ?? "",
        password: data.password,
      },
      {
        onSuccess: async (response) => {
          if (response.success) {
            // Forçar logout após redefinição bem-sucedida
            toast({
              title: t("common.success"),
              description: t("resetPassword.successMessage"),
            });

            // 1. Primeiro invalidar o token no servidor
            await invalidateServerToken();

            // 2. Forçar logout agressivo localmente
            await forceLogout();

            // 3. Redirecionar imediatamente para login com reload completo da página
            redirectToLogin();
          } else {
            toast({
              variant: "destructive",
              title: t("common.error"),
              description: response.message || t("resetPassword.genericError"),
            });
          }
        },
        onError: (error: any) => {
          toast({
            variant: "destructive",
            title: t("common.error"),
            description: resolveApiErrorMessage(error, t),
          });
        },
      },
    );
  };

  // Sem token na URL a tela não é acessível: com `enabled: !!token` a validação
  // nem roda (nenhum guard dispara) e o formulário renderizava aberto. Acesso
  // direto a /auth/reset-password sem ?token= volta pro login.
  if (!token) {
    return <Navigate to="/login" replace />;
  }

  // Mostrar carregamento durante a validação do token
  if (isValidatingToken) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center p-4 gap-4">
        <div className="flex justify-center">
          <img
            src="/assets/logo.png"
            alt="Prodgy Logo"
            className="h-12 w-auto"
          />
        </div>
        <div className="flex flex-col items-center justify-center gap-2">
          <LoadingSpinner size="lg" />
          <h2 className="text-lg">{t("resetPassword.validatingToken")}</h2>
          <p className="text-sm text-muted-foreground">
            {t("resetPassword.pleaseWait")}
          </p>
        </div>
      </div>
    );
  }

  // Mostrar erro se o token for inválido
  if (isTokenValidationError || (tokenValidation && !tokenValidation.success)) {
    // Usar o objeto de erro correto para obter detalhes
    const errorObj = tokenValidationError || tokenValidation;
    const errorDetails = getErrorDetails(errorObj, t);
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4 relative">
        <div className="w-full max-w-md space-y-4 animate-fade-in">
          <div className="flex justify-center mb-6">
            <img
              src="/assets/logo.png"
              alt="Prodgy Logo"
              className="h-12 w-auto"
            />
          </div>
          <Card className="w-full max-w-md">
            <CardHeader className="text-center">
              <CardTitle>{errorDetails.title}</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col items-center text-center space-y-6 py-6">
              <div className={`rounded-full p-6 ${errorDetails.colorClass}`}>
                {errorDetails.icon}
              </div>
              <p className="text-muted-foreground">
                {errorDetails.description}
              </p>
              <Button
                onClick={() => navigate("/login")}
                className="w-full sm:w-auto"
                variant="default"
              >
                {t("resetPassword.backToLogin")}
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Mostrar mensagem de sucesso quando a senha for redefinida
  if (isResetSuccess) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4 relative">
        <div className="w-full max-w-md space-y-4 animate-fade-in">
          <div className="flex justify-center mb-6">
            <img
              src="/assets/logo.png"
              alt={t("common.appName")}
              className="h-12 w-auto"
            />
          </div>
          <Card className="w-full max-w-md">
            <CardHeader className="text-center">
              <CardTitle>{t("resetPassword.successTitle")}</CardTitle>
              <CardDescription>
                {t("resetPassword.successDetail")}
              </CardDescription>
            </CardHeader>
            <CardContent className="flex justify-center py-6">
              <p>{t("resetPassword.redirecting")}</p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Formulário principal
  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4 relative">
      <div className="w-full max-w-md space-y-4 animate-fade-in">
        <div className="flex justify-center mb-6">
          <img
            src="/assets/logo.png"
            alt="Prodgy Logo"
            className="h-12 w-auto"
          />
        </div>
        <Card className="w-full">
          <CardHeader>
            <CardTitle className="text-center">
              {t("resetPassword.title")}
            </CardTitle>
            <CardDescription className="text-center">
              {t("resetPassword.description")}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Form {...form}>
              <form
                onSubmit={form.handleSubmit(onSubmit)}
                className="space-y-4"
              >
                {/* Campo de nova senha */}
                <FormField
                  control={form.control}
                  name="password"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t("resetPassword.newPassword")}</FormLabel>
                      <FormControl>
                        <div className="relative">
                          <Input
                            {...field}
                            type={isPasswordVisible ? "text" : "password"}
                            placeholder="********"
                            disabled={isResettingPassword}
                            onChange={(e) => {
                              field.onChange(e);
                              setCurrentPassword(e.target.value);
                            }}
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="absolute right-1 top-1"
                            onClick={() =>
                              setIsPasswordVisible(!isPasswordVisible)
                            }
                          >
                            {isPasswordVisible ? (
                              <EyeOff className="h-4 w-4" />
                            ) : (
                              <Eye className="h-4 w-4" />
                            )}
                          </Button>
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Campo de confirmação de senha */}
                <FormField
                  control={form.control}
                  name="confirmPassword"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        {t("resetPassword.confirmPassword")}
                      </FormLabel>
                      <FormControl>
                        <div className="relative">
                          <Input
                            {...field}
                            type={
                              isConfirmPasswordVisible ? "text" : "password"
                            }
                            placeholder="********"
                            disabled={isResettingPassword}
                            onChange={(e) => {
                              field.onChange(e);
                              setConfirmPassword(e.target.value);
                            }}
                            className={
                              confirmPassword.length === 0
                                ? ""
                                : passwordsMatch
                                  ? ""
                                  : "border-red-500 focus-visible:ring-red-500"
                            }
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="absolute right-1 top-1"
                            onClick={() =>
                              setIsConfirmPasswordVisible(
                                !isConfirmPasswordVisible,
                              )
                            }
                          >
                            {isConfirmPasswordVisible ? (
                              <EyeOff className="h-4 w-4" />
                            ) : (
                              <Eye className="h-4 w-4" />
                            )}
                          </Button>
                        </div>
                      </FormControl>
                      <FormMessage />
                      {confirmPassword.length > 0 && (
                        <div
                          className={`text-sm mt-1 flex items-center gap-2 ${
                            passwordsMatch
                              ? "text-green-600 dark:text-green-500"
                              : "text-red-600 dark:text-red-500"
                          }`}
                        >
                          {passwordsMatch ? (
                            <>
                              <Check className="h-4 w-4" />
                              <span>{t("resetPassword.passwordsMatch")}</span>
                            </>
                          ) : (
                            <>
                              <X className="h-4 w-4" />
                              <span>
                                {t("resetPassword.passwordsDoNotMatch")}
                              </span>
                            </>
                          )}
                        </div>
                      )}
                    </FormItem>
                  )}
                />

                {/* Requisitos de senha com validação visual */}
                <div className="text-sm">
                  <p className="font-medium mb-2 text-foreground">
                    {t("resetPassword.requirements")}
                  </p>
                  <ul className="space-y-2">
                    <li className="flex items-center gap-2 text-foreground">
                      {hasMinLength ? (
                        <Check className="h-4 w-4 text-green-500" />
                      ) : (
                        <div className="h-4 w-4 rounded-full border-2 border-muted-foreground/30" />
                      )}
                      <span>{t("resetPassword.reqMinLength")}</span>
                    </li>
                    <li className="flex items-center gap-2 text-foreground">
                      {hasUpperCase ? (
                        <Check className="h-4 w-4 text-green-500" />
                      ) : (
                        <div className="h-4 w-4 rounded-full border-2 border-muted-foreground/30" />
                      )}
                      <span>{t("resetPassword.reqUppercase")}</span>
                    </li>
                    <li className="flex items-center gap-2 text-foreground">
                      {hasLowerCase ? (
                        <Check className="h-4 w-4 text-green-500" />
                      ) : (
                        <div className="h-4 w-4 rounded-full border-2 border-muted-foreground/30" />
                      )}
                      <span>{t("resetPassword.reqLowercase")}</span>
                    </li>
                    <li className="flex items-center gap-2 text-foreground">
                      {hasNumber ? (
                        <Check className="h-4 w-4 text-green-500" />
                      ) : (
                        <div className="h-4 w-4 rounded-full border-2 border-muted-foreground/30" />
                      )}
                      <span>{t("resetPassword.reqNumber")}</span>
                    </li>
                    <li className="flex items-center gap-2 text-foreground">
                      {hasSpecialChar ? (
                        <Check className="h-4 w-4 text-green-500" />
                      ) : (
                        <div className="h-4 w-4 rounded-full border-2 border-muted-foreground/30" />
                      )}
                      <span>{t("resetPassword.reqSpecial")}</span>
                    </li>
                  </ul>
                </div>

                {/* Botão de envio */}
                <Button
                  type="submit"
                  className="w-full font-normal h-11 relative"
                  disabled={isResettingPassword}
                >
                  {isResettingPassword ? (
                    <div className="flex items-center justify-center">
                      <LoadingSpinner
                        className="h-5 w-5 text-white"
                        size="sm"
                      />
                    </div>
                  ) : (
                    <div className="flex items-center justify-center gap-2">
                      <Lock className="h-5 w-5" />
                      <span>{t("resetPassword.submit")}</span>
                    </div>
                  )}
                </Button>

                {/* Erro de redefinição com estilo melhorado */}
                {isResetError && (
                  <Alert variant="destructive" className="mt-4">
                    <AlertCircle className="h-4 w-4 mr-2" />
                    <AlertTitle>{t("common.error")}</AlertTitle>
                    <AlertDescription>
                      {resetError?.message || t("resetPassword.genericError")}
                    </AlertDescription>
                  </Alert>
                )}
              </form>
            </Form>
          </CardContent>
          <CardFooter className="flex justify-center">
            <Button variant="link" onClick={() => navigate("/login")}>
              {t("resetPassword.backToLogin")}
            </Button>
          </CardFooter>
        </Card>
      </div>
    </div>
  );
};

export default ResetPassword;
